/* This component layout is base for subnavigation component so if this layout changes subnavigation should change too! */
import {Box, Container, Flex, HStack, IconButton, Stack, Text} from '@chakra-ui/react'
import PropTypes from 'prop-types'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import React, {useMemo, useState, useRef} from 'react'
import {useIntl} from 'react-intl'
import {ArrowLeftRound2Icon, ArrowRightRound2Icon, PlayIcon} from '../../../components/icons'
import ImageGallery from '../../../components/image-gallery'
import ImageGallerySkeleton from '../../../components/image-gallery/skeleton'
import BVProductRating from '../../../components/bazaarvoice/rating'
import {hasBVProductRating} from '../../../components/bazaarvoice/utils'
import {isCustomizableProductSet} from '../../../utils/product-utils'
import {CRO_EXPERIMENTS, MAIN_IMAGES_IMAGE_TYPE} from '../../../constants'
import useElementScreenPosition from '../../../hooks/use-element-screen-position'
import PsdpCta from './psdp-cta'
import BadgeGroup from '../../../components/badge-group'
import {EngravingModal, useEngravingModal} from '../../../hooks/use-engraving-modal'
import {cloneDeep} from 'lodash'
import {trackPsdpSwipeProductTile} from '../../../tracking/analytics'
import PsdpCockpitRightColumn from './psdp-cockpit-right-column'
import CustomizablePsdpCockpitRightColumn from './customizable-psdp-cockpit-right-column'
import {
    filterSetCombinationImages,
    getProductSetSelectionImageName,
    reorderArray
} from '../../../utils/image-groups-utils'
import WishlistToggle from '../../../components/wishlist-toggle'
import {useCroSlot, useCroVariantActive} from '../../../components/cro'
import useCockpitGalleryImages from '../hooks/use-cockpit-gallery-images'
import CockpitInformation from './cockpit-information'
import useProductSpecialOffers from '../../../hooks/use-product-special-offers'

/**
 * Cockpit for product set detail page
 */
const ProductSetCockpit = ({
    derivedProduct,
    product,
    promotions,
    isGetPropsLoading,
    isPromotionsLoading,
    isVariantLoading,
    showZoomModal,
    onAddToCartClick,
    onHotspotClick,
    onRatingsClick
}) => {
    const {formatMessage} = useIntl()
    const engravingModal = useEngravingModal()
    const isCustomizableSet = isCustomizableProductSet(product)
    useCroSlot(
        isCustomizableSet ? `customizable_product_set_${product?.id}` : `product_set_${product?.id}`
    )

    const isCro63Active = useCroVariantActive(
        CRO_EXPERIMENTS.cro63PdpHideRating.experimentId,
        CRO_EXPERIMENTS.cro63PdpHideRating.variantId
    )

    const hasProductRating = useMemo(() => hasBVProductRating(product), [product])

    const {setRef: setHeadingRef} = useElementScreenPosition()
    const imagesContainerRef = useRef(null)

    const {videos, hotspots, isEngravable} = derivedProduct
    const productSpecialOffers = useProductSpecialOffers(product, undefined, 'pdp')

    const mainGalleryImages = useCockpitGalleryImages(product, derivedProduct)
    const mainHotspots = useMemo(() => hotspots?.[MAIN_IMAGES_IMAGE_TYPE], [hotspots])

    const selectionImageName = useMemo(() => {
        return getProductSetSelectionImageName(product)
    }, [product])

    const mainGalleryReordered = useMemo(() => {
        return filterSetCombinationImages(product, mainGalleryImages, selectionImageName)
    }, [mainGalleryImages, product, selectionImageName])

    const [hoveredImageData, setHoveredImageData] = useState(undefined)

    const getHoveredImageId = (imageName, hoveredImageData, gallery) => {
        const hoveredImageName = imageName.replace(
            hoveredImageData.currentId,
            hoveredImageData.hoveredId
        )
        return gallery
            .map((x) => {
                return x.link
            })
            .findIndex((link) => link?.includes(hoveredImageName))
    }

    const hoveredGalleryReordered = useMemo(() => {
        if (product?.c_selectionImageEnabled) {
            if (hoveredImageData) {
                // when hovering color swatch
                const scrollTo = getHoveredImageId(
                    selectionImageName,
                    hoveredImageData,
                    mainGalleryImages
                )
                if (scrollTo >= 0) {
                    //to trigger showing new image in the gallery

                    return reorderArray(mainGalleryImages, scrollTo)
                }
            }
        }
        return mainGalleryImages
    }, [selectionImageName, hoveredImageData, mainGalleryImages, product?.c_selectionImageEnabled])

    const stickyProps = {
        position: {lg: 'sticky'},
        top: 8
    }

    const leftSidebarSizeProps = {
        flexBasis: {
            lg: 'calc(25% + 0.5rem)',
            xl: 'calc(25% + 0.875rem)',
            '2xl': 'calc(25% + 0.75rem)'
        },
        minWidth: 0
    }

    const rightSidebarSizeProps = cloneDeep(leftSidebarSizeProps)
    rightSidebarSizeProps.flexBasis.lg = '32.113%'

    return (
        <Container layerStyle="page" variant="no-padding">
            <Flex direction="column" alignItems="stretch">
                <Box
                    paddingTop={{
                        base: 4,
                        lg: 8
                    }}
                    paddingBottom={12}
                    px={{base: 0, lg: 8}}
                >
                    <Flex
                        ref={setHeadingRef}
                        direction={{base: 'column', lg: 'row'}}
                        gap={{base: 6, '2xl': 8}}
                        alignItems={{lg: 'flex-start'}}
                    >
                        {/* mobile top / desktop left column */}
                        <Stack
                            spacing={6}
                            px={{base: 4, lg: 0}}
                            {...stickyProps}
                            {...leftSidebarSizeProps}
                        >
                            {/* Headline */}
                            <Stack spacing={2}>
                                {/* Title */}
                                <Text as={'h1'} textStyle="Regular 2XLarge">
                                    {product?.name}
                                </Text>

                                <Flex direction="row" flexWrap="wrap" gap={4}>
                                    {hasProductRating && !isCro63Active && (
                                        <BVProductRating
                                            product={product}
                                            variant="cockpit"
                                            onRatingsClick={onRatingsClick}
                                        />
                                    )}
                                    <BadgeGroup product={product} />
                                </Flex>
                            </Stack>

                            <Box display={{base: 'none', lg: 'block'}}>
                                <CockpitInformation
                                    product={product}
                                    isPromotionsLoading={isPromotionsLoading}
                                    productSpecialOffers={productSpecialOffers}
                                    highlightsExpanded
                                />
                            </Box>
                        </Stack>

                        {/* Product image - STOK-6322 */}
                        <Box ref={imagesContainerRef} flex={1} {...stickyProps}>
                            {!isVariantLoading ? (
                                <>
                                    <Box
                                        display={
                                            product?.c_selectionImageEnabled && hoveredImageData
                                                ? 'none'
                                                : null
                                        }
                                    >
                                        <ImageGallery
                                            allowAutoplay
                                            variant="cockpit"
                                            items={mainGalleryReordered}
                                            hotspots={mainHotspots}
                                            cursor={`url('${encodeURI(
                                                getAssetUrl('static/ico/zoom.svg')
                                            )}'),zoom-in`}
                                            navLeftIcon={<ArrowLeftRound2Icon boxSize={8} />}
                                            navRightIcon={<ArrowRightRound2Icon boxSize={8} />}
                                            preload
                                            showChildrenIn="overlay"
                                            showPagination
                                            imageProps={{
                                                widths: {base: '100vw', lg: '50vw'}
                                            }}
                                            onClick={
                                                showZoomModal &&
                                                ((_, image, index) => {
                                                    showZoomModal({
                                                        image,
                                                        index,
                                                        finalFocusRef: _.target
                                                    })
                                                })
                                            }
                                            onChange={() => trackPsdpSwipeProductTile(product)}
                                            onHotspotClick={onHotspotClick}
                                        >
                                            <Flex justifyItems="space-between">
                                                <HStack spacing={2} pointerEvents="auto">
                                                    <WishlistToggle
                                                        product={product}
                                                        variant="imageGallery"
                                                    />
                                                    {videos && videos?.length && (
                                                        <IconButton
                                                            variant="pillOnTile"
                                                            aria-label={formatMessage({
                                                                id: 'product_detail.button.assistive_msg.video',
                                                                defaultMessage: 'Video'
                                                            })}
                                                            onClick={(e) => {
                                                                showZoomModal({
                                                                    tab: 1,
                                                                    finalFocusRef: e.target
                                                                })
                                                            }}
                                                        >
                                                            <PlayIcon boxSize={6} />
                                                        </IconButton>
                                                    )}
                                                </HStack>
                                            </Flex>
                                        </ImageGallery>
                                    </Box>
                                    <Box
                                        display={
                                            product?.c_selectionImageEnabled && hoveredImageData
                                                ? null
                                                : 'none'
                                        }
                                    >
                                        <ImageGallery
                                            variant="cockpit"
                                            items={hoveredGalleryReordered}
                                            navLeftIcon={<ArrowLeftRound2Icon boxSize={8} />}
                                            navRightIcon={<ArrowRightRound2Icon boxSize={8} />}
                                            preload
                                            showChildrenIn="overlay"
                                            showPagination
                                            imageProps={{
                                                widths: {base: '100vw', lg: '50vw'}
                                            }}
                                        ></ImageGallery>
                                    </Box>
                                </>
                            ) : (
                                <ImageGallerySkeleton />
                            )}
                        </Box>

                        {/* mobile bottom / desktop right column */}
                        {isCustomizableSet ? (
                            <CustomizablePsdpCockpitRightColumn
                                derivedProduct={derivedProduct}
                                isGetPropsLoading={isGetPropsLoading}
                                isPromotionsLoading={isPromotionsLoading}
                                isVariantLoading={isVariantLoading}
                                product={product}
                                productSpecialOffers={productSpecialOffers}
                                promotions={promotions}
                                rightSidebarSizeProps={rightSidebarSizeProps}
                                stickyProps={stickyProps}
                                onAddToCartClick={onAddToCartClick}
                                onClickEngraving={engravingModal.onOpen}
                                onHoverChange={setHoveredImageData}
                            />
                        ) : (
                            <PsdpCockpitRightColumn
                                derivedProduct={derivedProduct}
                                isGetPropsLoading={isGetPropsLoading}
                                isPromotionsLoading={isPromotionsLoading}
                                isVariantLoading={isVariantLoading}
                                product={product}
                                productSpecialOffers={productSpecialOffers}
                                promotions={promotions}
                                rightSidebarSizeProps={rightSidebarSizeProps}
                                stickyProps={stickyProps}
                                onAddToCartClick={onAddToCartClick}
                                onClickEngraving={engravingModal.onOpen}
                                onHoverChange={setHoveredImageData}
                            />
                        )}
                        <PsdpCta
                            derivedProduct={derivedProduct}
                            isLoading={isGetPropsLoading || isPromotionsLoading || isVariantLoading}
                            product={product}
                            onAddToCartClick={onAddToCartClick}
                            promotions={promotions}
                        />
                        <Box display={{base: 'block', lg: 'none'}}>
                            <Stack px={{base: 4, lg: 0}} spacing={2}>
                                <CockpitInformation
                                    product={product}
                                    isPromotionsLoading={isPromotionsLoading}
                                    productSpecialOffers={productSpecialOffers}
                                />
                            </Stack>
                        </Box>
                    </Flex>
                </Box>
                {isEngravable && (
                    <EngravingModal
                        {...engravingModal}
                        isLoading={isGetPropsLoading || isPromotionsLoading || isVariantLoading}
                        product={product}
                        promotions={promotions}
                    />
                )}
            </Flex>
        </Container>
    )
}

ProductSetCockpit.propTypes = {
    derivedProduct: PropTypes.object,
    product: PropTypes.object,
    promotions: PropTypes.array,
    isGetPropsLoading: PropTypes.bool,
    isMasterLoading: PropTypes.bool,
    isPromotionsLoading: PropTypes.bool,
    isVariantLoading: PropTypes.bool,
    showZoomModal: PropTypes.func,
    notifyMe: PropTypes.func,
    onAddToCartClick: PropTypes.func,
    onHotspotClick: PropTypes.func,
    onMasterChange: PropTypes.func,
    onRatingsClick: PropTypes.func,
    onVariantChange: PropTypes.func
}

export default ProductSetCockpit
